import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import InstituteNavigation from '../../components/ui/InstituteNavigation';
import NotificationPanel from '../../components/ui/NotificationPanel';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import MetricCard from './components/MetricCard';
import BoothContentManager from './components/BoothContentManager';
import VisitorFeed from './components/VisitorFeed';
import LeadManagement from './components/LeadManagement';
import SessionScheduler from './components/SessionScheduler';
import ChatQueue from './components/ChatQueue';
import CourseManager from './components/CourseManager';
import TestimonialManager from './components/TestimonialManager';
import Icon from '../../components/AppIcon';
import AIHybridChatQueue from './components/AIHybridChatQueue';
import FAQManager from './components/FAQManager';
import AISettingsManager from './components/AISettingsManager';
import GroupSessionManager from './components/GroupSessionManager';
import { supabase } from '../../lib/supabase';
import { leadService } from '../../services/leadService';
import InstituteProfileTab from './components/InstituteProfileTab';

const InstituteDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [metricsData, setMetricsData] = useState({
    visitors: 0,
    activeChats: 0,
    scheduledSessions: 0,
    leads: 0,
    leadsThisWeek: 0
  });

  // Handle hash-based navigation for direct tab access
  useEffect(() => {
    const hash = location?.hash?.replace('#', '');
    if (hash && ['overview', 'courses', 'testimonials', 'faq', 'leads', 'sessions', 'group-sessions', 'content', 'chat', 'ai-chat', 'settings', 'profile']?.includes(hash)) {
      setActiveTab(hash);
    }
  }, [location?.hash]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/institute-login');
    }
  }, [user, navigate]);

  // Load real metrics
  useEffect(() => {
    if (!user?.id) return;
    const loadMetrics = async () => {
      try {
        const instituteProfile = userProfile?.institute_profiles?.[0];
        const instituteId = instituteProfile?.id;
        if (!instituteId) return;

        const today = new Date()?.toISOString()?.split('T')?.[0];

        const [visitsRes, sessionsRes, bookingsRes, leadsRes, weeklyLeadsCount] = await Promise.all([
          supabase?.from('booth_visits')?.select('id', { count: 'exact', head: true })?.eq('institute_id', user?.id),
          supabase?.from('counseling_sessions')?.select('id', { count: 'exact', head: true })?.eq('institute_id', user?.id)?.eq('status', 'scheduled'),
          supabase?.from('counselor_bookings')?.select('id', { count: 'exact', head: true })?.eq('institute_id', user?.id)?.gte('booking_date', today)?.in('status', ['confirmed', 'pending']),
          supabase?.from('leads')?.select('id', { count: 'exact', head: true })?.eq('institute_id', instituteId),
          leadService?.getLeadsThisWeek(instituteId)
        ]);

        const totalScheduled = (sessionsRes?.count || 0) + (bookingsRes?.count || 0);

        setMetricsData({
          visitors: visitsRes?.count || 0,
          activeChats: 0,
          scheduledSessions: totalScheduled,
          leads: leadsRes?.count || 0,
          leadsThisWeek: weeklyLeadsCount || 0
        });
      } catch (err) {
        console.error('Error loading institute metrics:', err);
      }
    };
    loadMetrics();
  }, [user?.id, userProfile]);

  if (!user) {
    return null;
  }

  const metrics = [
    {
      title: 'Total Visitors',
      value: metricsData?.visitors?.toLocaleString(),
      change: '',
      changeType: 'increase',
      trend: 'booth visits',
      icon: 'Users',
      iconColor: 'bg-primary/10'
    },
    {
      title: 'Active Chats',
      value: metricsData?.activeChats?.toLocaleString(),
      change: '',
      changeType: 'increase',
      trend: 'ongoing',
      icon: 'MessageSquare',
      iconColor: 'bg-accent/10'
    },
    {
      title: 'Scheduled Sessions',
      value: metricsData?.scheduledSessions?.toLocaleString(),
      change: '',
      changeType: 'increase',
      trend: 'upcoming',
      icon: 'Calendar',
      iconColor: 'bg-secondary/10'
    },
    {
      title: 'Leads This Week',
      value: metricsData?.leadsThisWeek?.toLocaleString(),
      change: `${metricsData?.leads || 0} total`,
      changeType: 'increase',
      trend: 'new leads',
      icon: 'Target',
      iconColor: 'bg-success/10'
    }
  ];

  const quickActions = [
    {
      label: 'Start Chat',
      icon: 'MessageSquare',
      variant: 'default',
      onClick: () => navigate('/chat-interface')
    },
    {
      label: 'Join Session',
      icon: 'Video',
      variant: 'outline',
      onClick: () => navigate('/institute-video-session')
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'courses', label: 'Courses', icon: 'BookOpen' },
    { id: 'testimonials', label: 'Testimonials', icon: 'MessageSquare' },
    { id: 'faq', label: 'FAQs', icon: 'HelpCircle' },
    { id: 'leads', label: 'Leads', icon: 'Users' },
    { id: 'sessions', label: 'Sessions', icon: 'Video' },
    { id: 'group-sessions', label: 'Group Sessions', icon: 'Users' },
    { id: 'content', label: 'Booth Content', icon: 'FileText' },
    { id: 'chat', label: 'Chat Queue', icon: 'MessageSquare' },
    { id: 'ai-chat', label: 'AI Chat Monitor', icon: 'Bot' },
    { id: 'settings', label: 'AI Settings', icon: 'Settings' },
    { id: 'profile', label: 'Profile', icon: 'UserCircle' }
  ];

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev =>
      prev?.map(notif =>
        notif?.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const handleClearAll = () => {
    setNotifications([]);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <InstituteNavigation />
      <main className="pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                Welcome back, {userProfile?.institute_profiles?.[0]?.institute_name}!
              </h1>
              <p className="text-muted-foreground">Manage your virtual booth and connect with students</p>
            </div>
            <div className="flex items-center gap-3">
              {userProfile?.institute_profiles?.[0]?.id && (
                <button
                  onClick={() => window.open(`/virtual-booth/${userProfile?.institute_profiles?.[0]?.id}`, '_blank')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium text-sm shadow-sm"
                  title="See how your booth looks to students"
                >
                  <Icon name="Eye" size={16} />
                  <span>Preview Booth</span>
                </button>
              )}
              <QuickActionToolbar actions={quickActions} position="relative" />
              <NotificationPanel
                notifications={notifications}
                onMarkAsRead={handleMarkAsRead}
                onClearAll={handleClearAll}
                userId={user?.id}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {metrics?.map((metric, index) => (
              <MetricCard key={index} {...metric} />
            ))}
          </div>

          {/* Tab Navigation */}
          <div className="bg-card border border-border rounded-lg mb-6 md:mb-8 overflow-hidden">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-smooth whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={tab?.icon} size={20} />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
                <div className="lg:col-span-2">
                  <BoothContentManager />
                </div>
                <div>
                  <VisitorFeed />
                </div>
              </div>
              <div className="mb-6 md:mb-8">
                <LeadManagement />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                <SessionScheduler />
                <ChatQueue />
              </div>
            </>
          )}

          {activeTab === 'courses' && (
            <div className="space-y-6">
              <CourseManager />
            </div>
          )}

          {activeTab === 'testimonials' && (
            <div className="space-y-6">
              <TestimonialManager />
            </div>
          )}

          {activeTab === 'faq' && (
            <div className="space-y-6">
              <FAQManager />
            </div>
          )}

          {activeTab === 'leads' && (
            <div className="space-y-6">
              <LeadManagement />
            </div>
          )}

          {activeTab === 'sessions' && (
            <div className="space-y-6">
              <SessionScheduler />
            </div>
          )}

          {activeTab === 'group-sessions' && (
            <div className="space-y-6">
              <GroupSessionManager />
            </div>
          )}

          {activeTab === 'content' && (
            <div className="space-y-6">
              <BoothContentManager />
            </div>
          )}

          {activeTab === 'chat' && <ChatQueue />}
          {activeTab === 'ai-chat' && <AIHybridChatQueue />}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <AISettingsManager />
            </div>
          )}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <InstituteProfileTab />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default InstituteDashboard;